import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import projectDashboardService from '../../services/projectDashboardService';
import moduleRealtimeService from '../../services/moduleRealtimeService';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ModuleAssignmentModal from './components/ModuleAssignmentModal';

const ProductionLineDashboard = () => {
  const { projectId } = useParams();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLine, setSelectedLine] = useState('main-1');
  const [viewMode, setViewMode] = useState('floor'); // floor or list
  const [assignmentModal, setAssignmentModal] = useState({
    isOpen: false,
    lineInfo: null,
    slotIndex: null
  });
  const [lineAssignments, setLineAssignments] = useState({}); // Store manual assignments

  // Load modules from project
  useEffect(() => {
    const loadModules = async () => {
      if (!projectId) return;
      
      try {
        setLoading(true);
        const allData = await projectDashboardService?.getAllDashboardData(projectId);
        setModules(allData?.modules || []);
      } catch (err) {
        console.error('Error loading modules:', err);
      } finally {
        setLoading(false);
      }
    };

    loadModules();
  }, [projectId]);

  // Real-time subscriptions for modules
  useEffect(() => {
    if (!projectId) return;

    const subscription = moduleRealtimeService?.subscribeToModuleChanges(projectId, (payload) => {
      if (payload?.event === 'INSERT') {
        setModules(prev => [...prev, payload?.data]);
      } else if (payload?.event === 'UPDATE') {
        setModules(prev => prev?.map(m => m?.id === payload?.data?.id ? payload?.data : m));
      } else if (payload?.event === 'DELETE') {
        setModules(prev => prev?.filter(m => m?.id !== payload?.data?.id));
      }
    });

    return () => {
      if (subscription) {
        moduleRealtimeService?.unsubscribe(subscription);
      }
    };
  }, [projectId]);

  // Production lines configuration
  const productionLines = [
    { id: 'main-1', name: 'Main Production Line 1', type: 'main', capacity: 25, color: '#3b82f6' },
    { id: 'main-2', name: 'Main Production Line 2', type: 'main', capacity: 25, color: '#8b5cf6' },
    { id: 'main-3', name: 'Main Production Line 3', type: 'main', capacity: 25, color: '#06b6d4' },
    { id: 'sub-lgs-cold', name: 'LGS Cold Forming Walls', type: 'sub', capacity: 10, color: '#10b981' },
    { id: 'sub-lgs-assembly', name: 'LGS Assembly', type: 'sub', capacity: 10, color: '#f59e0b' },
    { id: 'sub-fabrication', name: 'Fabrication Steel', type: 'sub', capacity: 8, color: '#ef4444' },
    { id: 'sub-electrical', name: 'Electrical', type: 'sub', capacity: 8, color: '#eab308' },
    { id: 'sub-mechanical', name: 'Mechanical', type: 'sub', capacity: 8, color: '#6366f1' },
    { id: 'sub-plumbing', name: 'Plumbing', type: 'sub', capacity: 8, color: '#14b8a6' },
    { id: 'sub-drywall', name: 'Dry Wall', type: 'sub', capacity: 8, color: '#a855f7' },
    { id: 'sub-masonry', name: 'Masonry', type: 'sub', capacity: 8, color: '#f97316' }
  ];

  // Distribute modules across production lines based on status and progress
  const distributeModules = () => {
    const distribution = {};
    productionLines?.forEach(line => {
      distribution[line?.id] = [];
    });

    // First, apply manual assignments
    Object.keys(lineAssignments)?.forEach(lineId => {
      const assignments = lineAssignments?.[lineId] || {};
      Object.keys(assignments)?.forEach(slotIndex => {
        const assignment = assignments?.[slotIndex];
        if (assignment?.module) {
          if (!distribution?.[lineId]) distribution[lineId] = [];
          distribution[lineId][parseInt(slotIndex)] = assignment?.module;
        }
      });
    });

    // Then fill remaining slots with auto-distributed modules (if needed)
    // This part can be customized based on requirements

    return distribution;
  };

  const moduleDistribution = distributeModules();

  // Handle opening assignment modal
  const handleSlotClick = (line, slotIndex) => {
    setAssignmentModal({
      isOpen: true,
      lineInfo: line,
      slotIndex
    });
  };

  // Handle module assignment
  const handleModuleAssign = (assignmentData) => {
    const { lineId, slotIndex, module, project } = assignmentData;
    
    setLineAssignments(prev => ({
      ...prev,
      [lineId]: {
        ...(prev?.[lineId] || {}),
        [slotIndex]: { module, project }
      }
    }));
  };

  // Calculate line statistics
  const getLineStats = (lineId) => {
    const lineModules = moduleDistribution?.[lineId] || [];
    const total = lineModules?.length;
    const completed = lineModules?.filter(m => m?.completed > 0)?.length;
    const onFloor = lineModules?.filter(m => m?.onFloor > 0)?.length;
    const pending = lineModules?.filter(m => m?.pending > 0)?.length;
    const progress = total > 0 ? Math.round((completed / total) * 100) : 0;
    
    return { total, completed, onFloor, pending, progress };
  };

  // Get status color based on progress
  const getStatusColor = (progress) => {
    if (progress >= 80) return 'var(--color-success)';
    if (progress >= 50) return 'var(--color-warning)';
    return 'var(--color-error)';
  };

  const mainLines = productionLines?.filter(l => l?.type === 'main');
  const subLines = productionLines?.filter(l => l?.type === 'sub');

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <div 
        className="lg:ml-60" 
        style={{ 
          marginLeft: projectId 
            ? 'calc(var(--sidebar-width, 240px) + var(--project-submenu-width, 192px))' 
            : 'var(--sidebar-width, 240px)' 
        }}
      >
        {/* Header */}
        <div className="bg-card border-b border-border px-4 md:px-6 lg:px-8 py-4">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-1">
                Production Line Dashboard
              </h1>
              <p className="text-sm text-muted-foreground">
                Visual factory floor layout with module tracking across production lines
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'floor' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('floor')}
              >
                <Icon name="LayoutGrid" size={16} />
                <span className="ml-2">Floor View</span>
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <Icon name="List" size={16} />
                <span className="ml-2">List View</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Factory Floor Layout */}
        <div className="p-4 md:p-6 lg:p-8">
          {loading ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <Icon name="Loader2" size={48} color="var(--color-primary)" className="animate-spin mx-auto mb-4" />
                <p className="text-muted-foreground">Loading production lines...</p>
              </div>
            </div>
          ) : viewMode === 'floor' ? (
            <div className="space-y-8">
              {/* Main Production Lines */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Factory" size={24} color="var(--color-primary)" />
                  <h2 className="text-xl font-semibold text-foreground">Main Production Lines</h2>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {mainLines?.map(line => {
                    const stats = getLineStats(line?.id);
                    const lineModules = moduleDistribution?.[line?.id] || [];
                    
                    return (
                      <div 
                        key={line?.id}
                        className="bg-card rounded-xl border-2 border-border hover:border-primary transition-all cursor-pointer"
                        onClick={() => setSelectedLine(line?.id)}
                      >
                        {/* Line Header */}
                        <div 
                          className="px-4 py-3 rounded-t-xl border-b border-border"
                          style={{ backgroundColor: `${line?.color}15` }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: line?.color }}
                              />
                              <h3 className="font-semibold text-foreground">{line?.name}</h3>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {stats?.total}/{line?.capacity} modules
                            </span>
                          </div>
                        </div>
                        {/* Progress Bar */}
                        <div className="px-4 py-3 border-b border-border">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-muted-foreground">Overall Progress</span>
                            <span className="text-sm font-semibold" style={{ color: getStatusColor(stats?.progress) }}>
                              {stats?.progress}%
                            </span>
                          </div>
                          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all duration-500"
                              style={{ 
                                width: `${stats?.progress}%`,
                                backgroundColor: getStatusColor(stats?.progress)
                              }}
                            />
                          </div>
                        </div>
                        {/* Module Status Grid */}
                        <div className="p-4">
                          <div className="grid grid-cols-5 gap-2 mb-3">
                            {Array?.from({ length: line?.capacity })?.map((_, idx) => {
                              const module = lineModules?.[idx];
                              const hasModule = !!module;
                              const moduleProgress = module ? 
                                (module?.completed / (module?.quantity || 1)) * 100 : 0;
                              
                              return (
                                <div
                                  key={idx}
                                  onClick={() => handleSlotClick(line, idx)}
                                  className="aspect-square rounded-lg border-2 transition-all hover:scale-110 cursor-pointer"
                                  style={{
                                    backgroundColor: hasModule ? `${line?.color}20` : 'var(--color-muted)',
                                    borderColor: hasModule ? line?.color : 'var(--color-border)',
                                    opacity: hasModule ? 1 : 0.3
                                  }}
                                  title={hasModule ? `${module?.name} - ${Math.round(moduleProgress)}% complete` : 'Click to assign module'}
                                >
                                  {hasModule ? (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <Icon 
                                        name={moduleProgress >= 100 ? 'CheckCircle2' : moduleProgress > 0 ? 'Clock' : 'Package'} 
                                        size={16} 
                                        color={line?.color}
                                      />
                                    </div>
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                      <Icon name="Plus" size={16} color="var(--color-muted-foreground)" />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* Stats */}
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div className="bg-muted rounded-lg p-2">
                              <div className="text-xs text-muted-foreground mb-1">Pending</div>
                              <div className="text-lg font-semibold text-foreground">{stats?.pending}</div>
                            </div>
                            <div className="bg-muted rounded-lg p-2">
                              <div className="text-xs text-muted-foreground mb-1">On Floor</div>
                              <div className="text-lg font-semibold text-foreground">{stats?.onFloor}</div>
                            </div>
                            <div className="bg-muted rounded-lg p-2">
                              <div className="text-xs text-muted-foreground mb-1">Completed</div>
                              <div className="text-lg font-semibold text-success">{stats?.completed}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Sub Production Lines */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Boxes" size={24} color="var(--color-accent)" />
                  <h2 className="text-xl font-semibold text-foreground">Sub Production Lines</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {subLines?.map(line => {
                    const stats = getLineStats(line?.id);
                    const lineModules = moduleDistribution?.[line?.id] || [];
                    
                    return (
                      <div 
                        key={line?.id}
                        className="bg-card rounded-xl border-2 border-border hover:border-primary transition-all cursor-pointer"
                        onClick={() => setSelectedLine(line?.id)}
                      >
                        {/* Line Header */}
                        <div 
                          className="px-3 py-2 rounded-t-xl border-b border-border"
                          style={{ backgroundColor: `${line?.color}15` }}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <div 
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: line?.color }}
                            />
                            <h3 className="text-sm font-semibold text-foreground">{line?.name}</h3>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">
                              {stats?.total}/{line?.capacity}
                            </span>
                            <span className="text-xs font-semibold" style={{ color: getStatusColor(stats?.progress) }}>
                              {stats?.progress}%
                            </span>
                          </div>
                        </div>
                        {/* Module Grid */}
                        <div className="p-3">
                          <div className="grid grid-cols-4 gap-1.5 mb-2">
                            {Array?.from({ length: line?.capacity })?.map((_, idx) => {
                              const module = lineModules?.[idx];
                              const hasModule = !!module;
                              const moduleProgress = module ? 
                                (module?.completed / (module?.quantity || 1)) * 100 : 0;
                              
                              return (
                                <div
                                  key={idx}
                                  onClick={() => handleSlotClick(line, idx)}
                                  className="aspect-square rounded border transition-all hover:scale-110 cursor-pointer"
                                  style={{
                                    backgroundColor: hasModule ? `${line?.color}30` : 'var(--color-muted)',
                                    borderColor: hasModule ? line?.color : 'var(--color-border)',
                                    opacity: hasModule ? 1 : 0.3
                                  }}
                                  title={hasModule ? `${module?.name}` : 'Click to assign'}
                                >
                                  {hasModule && moduleProgress >= 100 && (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <Icon name="Check" size={10} color={line?.color} />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* Progress Bar */}
                          <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all duration-500"
                              style={{ 
                                width: `${stats?.progress}%`,
                                backgroundColor: line?.color
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Legend */}
              <div className="bg-card rounded-xl border border-border p-4">
                <h3 className="text-sm font-semibold text-foreground mb-3">Legend</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex items-center gap-2">
                    <Icon name="Package" size={16} color="var(--color-muted-foreground)" />
                    <span className="text-sm text-muted-foreground">Pending Module</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Clock" size={16} color="var(--color-warning)" />
                    <span className="text-sm text-muted-foreground">In Progress</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
                    <span className="text-sm text-muted-foreground">Completed</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded border-2 border-border bg-muted opacity-30" />
                    <span className="text-sm text-muted-foreground">Empty Slot</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* List View */
            (<div className="space-y-4">
              {productionLines?.map(line => {
                const stats = getLineStats(line?.id);
                const lineModules = moduleDistribution?.[line?.id] || [];
                
                return (
                  <div key={line?.id} className="bg-card rounded-xl border border-border overflow-hidden">
                    {/* Line Header */}
                    <div 
                      className="px-4 py-3 border-b border-border"
                      style={{ backgroundColor: `${line?.color}10` }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: line?.color }}
                          />
                          <div>
                            <h3 className="font-semibold text-foreground">{line?.name}</h3>
                            <p className="text-xs text-muted-foreground">
                              {line?.type === 'main' ? 'Main Production Line' : 'Sub Production Line'} • 
                              Capacity: {line?.capacity} modules
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-semibold" style={{ color: getStatusColor(stats?.progress) }}>
                            {stats?.progress}%
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {stats?.completed}/{stats?.total} completed
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Modules List */}
                    <div className="p-4">
                      {lineModules?.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {lineModules?.map(module => {
                            const moduleProgress = (module?.completed / (module?.quantity || 1)) * 100;
                            
                            return (
                              <div 
                                key={module?.id}
                                className="bg-muted rounded-lg p-3 border border-border hover:border-primary transition-all"
                              >
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-semibold text-foreground truncate">
                                      {module?.name}
                                    </h4>
                                    <p className="text-xs text-muted-foreground">
                                      Qty: {module?.quantity}
                                    </p>
                                  </div>
                                  <Icon 
                                    name={moduleProgress >= 100 ? 'CheckCircle2' : moduleProgress > 0 ? 'Clock' : 'Package'} 
                                    size={16} 
                                    color={moduleProgress >= 100 ? 'var(--color-success)' : moduleProgress > 0 ? 'var(--color-warning)' : 'var(--color-muted-foreground)'}
                                  />
                                </div>
                                <div className="space-y-1">
                                  <div className="flex items-center justify-between text-xs">
                                    <span className="text-muted-foreground">Progress</span>
                                    <span className="font-semibold text-foreground">{Math.round(moduleProgress)}%</span>
                                  </div>
                                  <div className="w-full h-1.5 bg-background rounded-full overflow-hidden">
                                    <div 
                                      className="h-full rounded-full transition-all duration-500"
                                      style={{ 
                                        width: `${moduleProgress}%`,
                                        backgroundColor: line?.color
                                      }}
                                    />
                                  </div>
                                  <div className="grid grid-cols-3 gap-1 mt-2 text-xs">
                                    <div>
                                      <span className="text-muted-foreground">Pending:</span>
                                      <span className="ml-1 font-semibold text-foreground">{module?.pending}</span>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground">Floor:</span>
                                      <span className="ml-1 font-semibold text-foreground">{module?.onFloor}</span>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground">Done:</span>
                                      <span className="ml-1 font-semibold text-success">{module?.completed}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <Icon name="Package" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
                          <p className="text-sm">No modules assigned to this line</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>)
          )}
        </div>
      </div>
      {/* Module Assignment Modal */}
      <ModuleAssignmentModal
        isOpen={assignmentModal?.isOpen}
        onClose={() => setAssignmentModal({ isOpen: false, lineInfo: null, slotIndex: null })}
        lineInfo={assignmentModal?.lineInfo}
        slotIndex={assignmentModal?.slotIndex}
        onAssign={handleModuleAssign}
      />
    </div>
  );
};

export default ProductionLineDashboard;